import React from "react";
import { useForm } from "react-hook-form";
import InputField from "../resuableComponents/InputField";
import SmInputField from "../resuableComponents/SmInputField";
import SelectBox from "../resuableComponents/SelectBox";
import DatePickerField from "../resuableComponents/DatePickerField";
import dayjs from "dayjs";
import "./RegistrationForm.css"; // External CSS
import { toast } from "react-toastify";
import { getBranches } from "../../api/admin/branch";
import { registerPatient } from "../../api/patient";
import axios from "axios";
import { handleApiError } from "../../utils/errorHandler";

const RegistrationForm = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const [branches, setBranches] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [userBranch, setUserBranch] = React.useState(null);
  const [showCampaignFields, setShowCampaignFields] = React.useState(false);

  React.useEffect(() => {
    // Get logged-in user's branch
    const authData = JSON.parse(localStorage.getItem("authData") || "null");
    if (authData?.user?.branch) {
      setUserBranch(authData.user.branch);
    }

    const fetchBranches = async () => {
      try {
        const response = await getBranches();
        if (response.data && Array.isArray(response.data.result)) {
          setBranches(response.data.result.map(b => ({ value: b.name, label: b.name })));
        }
      } catch (error) {
        console.error("Failed to fetch branches:", error);
      }
    };
    fetchBranches();
  }, []);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Prepare payload
      const payload = {
        name: data.fullName,
        phone: data.phone,
        email: data.email,
        age: data.age,
        gender: data.sex,
        dateOfBirth: data.dateOfBirth,
        maritalStatus: data.maritalStatus,
        husbandFatherName: data.husbandorFatherName,
        address: data.address,
        taluka: data.taluka,
        city: data.city,
        state: data.state,
        branch: userBranch?.name || data.branch, // Auto-assign if user has branch, else use selected
        housePhone: data.housePhone,
        officePhone: data.officePhone,
        is_campaign_referred: showCampaignFields && data.is_campaign_referred ? true : false,
        campaign_name: data.campaign_name || undefined,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}register-patient`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${JSON.parse(localStorage.getItem("authData") || "{}").accessToken}`,
          }
        }
      );

      toast.success("Patient registered successfully!");
      // Reset form would go here
    } catch (error) {
      console.error("Registration error:", error);
      handleApiError(error, "Failed to register patient");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reg-container">
      <div className="reg-header">
        <h2>Registration Form</h2>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="reg-form">
        <div className="row g-4 mb-2">
          <InputField
            label="Full Name"
            id="fullName"
            type="text"
            placeholder="Enter your full name"
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.fullName?.message}
          />
          <DatePickerField
            label="Date of Birth"
            id="dateOfBirth"
            control={control}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.dateOfBirth?.message}
            maxDate={dayjs()}
          />
        </div>
        <div className="row g-4 mb-3">
          <SmInputField
            label="Age"
            id="age"
            type="number"
            placeholder="Enter your age"
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.age?.message}
          />
          <SelectBox
            id="sex"
            label="Sex"
            options={[
              { value: "male", label: "Male" },
              { value: "female", label: "Female" },
              { value: "other", label: "Other" },
            ]}
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.sex?.message}
          />
          <SelectBox
            id="maritalStatus"
            label="Marital Status"
            options={[
              { value: "single", label: "Single" },
              { value: "married", label: "Married" },
            ]}
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.maritalStatus?.message}
          />
        </div>
        <div className="row g-4 mb-3">
          <InputField
            label="Husband/Father Name"
            id="husbandorFatherName"
            type="text"
            placeholder="Enter husband's or father's name"
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.husbandorFatherName?.message}
          />
          <InputField
            label="Address"
            id="address"
            type="text"
            placeholder="Enter your address"
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.address?.message}
          />
        </div>
        <div className="row g-4 mb-3">
          <SmInputField
            label="Taluka"
            id="taluka"
            type="text"
            placeholder="Enter your taluka"
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.taluka?.message}
          />
          <SmInputField
            label="City"
            id="city"
            type="text"
            placeholder="Enter your city"
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.city?.message}
          />
          <SmInputField
            label="State"
            id="state"
            type="text"
            placeholder="Enter your state"
            register={register}
            validationRules={{ required: "This field is required" }}
            errorMessage={errors.state?.message}
          />
        </div>
        <div className="row g-4 mb-3">
          <InputField
            label="Phone"
            id="phone"
            type="tel"
            placeholder="Enter your phone number"
            register={register}
            validationRules={{
              required: "This field is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Phone number must be 10 digits",
              },
            }}
            errorMessage={errors.phone?.message}
          />
          <InputField
            label="Email"
            id="email"
            type="email"
            placeholder="Enter your email"
            register={register}
            validationRules={{
              required: "This field is required",
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: "Invalid email address",
              },
            }}
            errorMessage={errors.email?.message}
          />
          {!userBranch && (
            <SelectBox
              id="branch"
              label="Branch"
              options={branches}
              register={register}
              validationRules={{ required: "This field is required" }}
              errorMessage={errors.branch?.message}
            />
          )}
          {userBranch && (
            <div className="auto-branch-info">
              <label>Branch</label>
              <input
                type="text"
                value={userBranch.name || userBranch}
                disabled
                className="form-control"
              />
              <small className="text-muted">Auto-assigned to your branch</small>
            </div>
          )}
        </div>
        <div className="row g-4 mb-3">
          <InputField
            label="House Phone"
            id="housePhone"
            type="tel"
            placeholder="House Phone"
            register={register}
            validationRules={{
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Phone number must be 10 digits",
              },
            }}
            errorMessage={errors.housePhone?.message}
          />
          <InputField
            label="Office Phone"
            id="officePhone"
            type="tel"
            placeholder="Office Phone"
            register={register}
            validationRules={{
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Phone number must be 10 digits",
              },
            }}
            errorMessage={errors.officePhone?.message}
          />
        </div>

        {/* Campaign Referral Section */}
        <div className="row g-4 mb-3">
          <div className="form-check">
            <input
              type="checkbox"
              id="is_campaign_referred"
              className="form-check-input"
              {...register("is_campaign_referred")}
              onChange={(e) => setShowCampaignFields(e.target.checked)}
            />
            <label htmlFor="is_campaign_referred" className="form-check-label">
              Campaign Referred Patient
            </label>
          </div>
        </div>

        {showCampaignFields && (
          <div className="row g-4 mb-3">
            <InputField
              label="Campaign Name"
              id="campaign_name"
              type="text"
              placeholder="Enter campaign name (e.g., Summer Health Camp)"
              register={register}
              validationRules={{
                required: showCampaignFields ? "Campaign name is required for campaign referred patients" : false,
              }}
              errorMessage={errors.campaign_name?.message}
            />
          </div>
        )}

        <div className="text-center mt-4">
          <button type="submit" className="reg-submit-btn" disabled={loading}>
            {loading ? "Registering..." : "Register Now"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;
